import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Mail, Phone, MapPin, Send, CheckCircle2, XCircle } from "lucide-react";
import { faqData } from "../mockData";
import api from "../api";
import { useLayoutEffect } from "react";

const Help = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [ticketForm, setTicketForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    success: false,
    title: "",
    description: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const response = await api.post("/support/submit", ticketForm);

      if (response.data.success) {
        setModalData({
          success: true,
          title: "Ticket Submitted Successfully!",
          description: `Ticket ID: ${response.data.ticketId}. Check your email for confirmation.`,
        });
        setShowModal(true);
        setTicketForm({ name: "", email: "", subject: "", message: "" });
      } else {
        setModalData({
          success: false,
          title: "Submission Failed",
          description: response.data.message || "Failed to submit ticket.",
        });
        setShowModal(true);
      }
    } catch (error) {
      setModalData({
        success: false,
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to submit ticket. Please try again.",
      });
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTicketForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen py-20 px-6 bg-gradient-to-b from-gray-950 via-black to-gray-900 text-gray-100 overflow-hidden relative">
      {/* Background Glow */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-red-600/10 blur-[180px] rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-green-500/10 blur-[180px] rounded-full"></div>

      {/* Success/Error Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-gradient-to-br from-gray-900 to-black border border-red-600/40">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              {modalData.success ? (
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
              )}
            </div>
            <DialogTitle
              className={`text-center text-xl ${
                modalData.success ? "text-green-400" : "text-red-400"
              }`}
            >
              {modalData.title}
            </DialogTitle>
            <DialogDescription className="text-center text-gray-300 text-base">
              {modalData.description}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              onClick={() => setShowModal(false)}
              className={`${
                modalData.success
                  ? "bg-gradient-to-r from-green-600 to-emerald-600"
                  : "bg-gradient-to-r from-red-600 to-rose-600"
              } hover:opacity-90 transition-all duration-300`}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Get assistance with your{" "}
            <span className="text-red-400 font-semibold">Udbhav journey</span>.
            We're here to{" "}
            <span className="text-green-400 font-semibold">
              help you succeed!
            </span>
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <Card className="border border-red-600/40 bg-gradient-to-br from-red-950/40 to-black sticky top-24 hover:shadow-[0_0_25px_rgba(220,38,38,0.3)] transition-all duration-500">
              <CardHeader>
                <CardTitle className="text-white">
                  Contact Information
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Reach out to us through any of these channels
                </CardDescription>
              </CardHeader>
<CardContent className="space-y-6">
  {[
    {
      icon: <Mail className="w-6 h-6 text-red-400" />,
      title: "Email",
      values: [
        {
          value: "udbhav@oges.co",
          href: "mailto:udbhav@oges.co",
        },
        {
          value: "vyas_rishi@ongc.co.in",
          href: "mailto:vyas_rishi@ongc.co.in",
        },
      ],
    },
    {
      icon: <Phone className="w-6 h-6 text-green-400" />,
      title: "Phone",
      value: "+91-98211 06466",
      href: "tel:+919821106466",
    },
    {
      icon: <MapPin className="w-6 h-6 text-gray-400" />,
      title: "Address",
      value: "ONGC 12th Floor, Scope Minar, Laxmi Nagar, New Delhi, India",
    },
  ].map((info, i) => (
    <div key={i} className="flex items-start space-x-4">
      <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse-slow">
        {info.icon}
      </div>
      <div>
        <div className="font-semibold text-white mb-1">{info.title}</div>

        {/* ✅ Handle multiple emails */}
        {info.values ? (
          <div className="flex flex-col space-y-1">
            {info.values.map((mail, idx) => (
              <a
                key={idx}
                href={mail.href}
                className="text-red-400 hover:text-green-400 transition-all duration-300"
              >
                {mail.value}
              </a>
            ))}
          </div>
        ) : info.href ? (
          <a
            href={info.href}
            className="text-red-400 hover:text-green-400 transition-all duration-300"
          >
            {info.value}
          </a>
        ) : (
          <p className="text-gray-400 text-sm">{info.value}</p>
        )}
      </div>
    </div>
  ))}
</CardContent>

            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Support Ticket Form */}
            <Card className="border border-red-600/40 bg-gradient-to-br from-gray-900/60 to-black hover:shadow-[0_0_25px_rgba(220,38,38,0.3)] transition-all duration-500">
              <CardHeader>
                <CardTitle className="text-white">
                  Submit a Support Ticket
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Fill out the form below, and our support team will respond
                  promptly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={ticketForm.name}
                        onChange={handleChange}
                        required
                        className="mt-2 bg-gray-800 border-red-600/40 focus:border-green-500 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={ticketForm.email}
                        onChange={handleChange}
                        required
                        className="mt-2 bg-gray-800 border-red-600/40 focus:border-green-500 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-gray-300">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={ticketForm.subject}
                      onChange={handleChange}
                      required
                      className="mt-2 bg-gray-800 border-red-600/40 focus:border-green-500 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-gray-300">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={ticketForm.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-2 bg-gray-800 border-red-600/40 focus:border-green-500 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-red-600 to-green-600 hover:opacity-90 transition-all duration-300"
                    disabled={isSubmitting}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? "Submitting..." : "Submit Ticket"}
                  </Button>
                </form>
              </CardContent>
            </Card>

  {/* FAQ Section */}
<section className="py-20 px-6 bg-black text-white relative overflow-hidden">
  <div className="max-w-7xl mx-auto">

    {/* Section Heading */}
    <div className="mb-12 text-center">
      <h2 className="text-2xl md:text-4xl font-bold
        bg-gradient-to-r from-red-500 via-emerald-400 to-green-500
        bg-clip-text text-transparent">
        Frequently Asked Questions
      </h2>
    </div>

    {/* FAQ Accordion */}
    <Accordion type="single" collapsible className="space-y-4">
      {faqData.map((faq, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          className="
            border border-white/10
            rounded-xl
            bg-gradient-to-br from-gray-900/70 to-black
            hover:border-red-500/40
            transition-all duration-300
          "
        >
          <AccordionTrigger
            className="
              px-6 py-5
              text-left text-lg font-semibold
              text-gray-200
              hover:no-underline
              hover:text-emerald-400
              transition-colors
            "
          >
            {faq.question}
          </AccordionTrigger>

          <AccordionContent
            className="
              px-6 pb-5
              text-gray-400
              leading-relaxed
            "
          >
            <div
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>

  </div>
</section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
